import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faPencil,
  faPlus,
  faWandMagicSparkles,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import clsx from "clsx";
import type { GuardianRule, IgnoreRule, LlmRule, RuleAlertSeverity, RuleOutcomeCounts } from "@/services/api/guardiansv2/types";
import { Checkbox } from "@/components/ui/checkbox/Checkbox";
import TextArea from "@/components/ui/form-input/TextArea";
import FormInput from "@/components/ui/form-input";

function newId(): string {
  return crypto.randomUUID().slice(0, 12);
}

function rulePreview(text: string, maxLength = 96): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "No rule text yet";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1)}…`;
}

function ruleSeverity(rule: LlmRule): RuleAlertSeverity {
  return rule.severity === "amber" ? "amber" : "red";
}

function formatRuleStats(stats: RuleOutcomeCounts): string {
  const parts: string[] = [];
  if (stats.passed > 0) parts.push(`${stats.passed} pass`);
  if (stats.failed > 0) parts.push(`${stats.failed} fail`);
  if (stats.error > 0) parts.push(`${stats.error} error`);
  if (stats.ignored > 0) parts.push(`${stats.ignored} ignore`);
  return parts.join(" · ");
}

function SeverityToggle({
  value,
  onChange,
}: {
  value: RuleAlertSeverity;
  onChange: (value: RuleAlertSeverity) => void;
}) {
  return (
    <div className="flex h-8 items-center gap-1 rounded-md border border-white/10 bg-slate-900/40 p-0.5">
      {(["amber", "red"] as const).map((level) => (
        <button
          key={level}
          type="button"
          onClick={() => onChange(level)}
          className={clsx(
            "rounded px-2 py-1 text-[11px] font-medium capitalize transition-colors",
            value === level
              ? level === "amber"
                ? "bg-amber-500/20 text-amber-200"
                : "bg-rose-500/20 text-rose-200"
              : "text-slate-500 hover:text-slate-300"
          )}
          data-testid={`severity-${level}`}
        >
          {level}
        </button>
      ))}
    </div>
  );
}

function RuleRemoveButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Remove rule"
      className={clsx(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/5 hover:text-rose-400",
        className
      )}
      data-testid="rule-remove"
    >
      <FontAwesomeIcon icon={faXmark} className="text-sm" />
    </button>
  );
}

function RuleToggle({
  label,
  checked,
  onChange,
  title,
  testId,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  title?: string;
  testId?: string;
}) {
  return (
    <label
      className="flex h-8 items-center gap-2 whitespace-nowrap text-xs text-slate-400"
      title={title}
    >
      <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span>{label}</span>
    </label>
  );
}

export interface AddRuleMenuProps {
  onAdd: (rule: GuardianRule) => void;
  onGenerate: () => void;
}

export function AddRuleMenu({ onAdd, onGenerate }: AddRuleMenuProps) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() =>
          onAdd({
            id: newId(),
            kind: "llm",
            text: "",
            label: "New alert rule",
            enabled: true,
            notify: false,
            severity: "red",
          })
        }
        className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700/80"
        data-testid="add-llm-rule"
      >
        <FontAwesomeIcon icon={faPlus} className="text-xs" />
        Add alert rule
      </button>
      <button
        type="button"
        onClick={onGenerate}
        className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700/80"
        data-testid="generate-rules"
      >
        <FontAwesomeIcon icon={faWandMagicSparkles} className="text-xs" />
        Generate
      </button>
    </div>
  );
}

export interface LlmRuleRowProps {
  rule: LlmRule;
  editing: boolean;
  stats?: RuleOutcomeCounts;
  readOnly?: boolean;
  onChange: (rule: LlmRule) => void;
  onRemove: () => void;
  onStartEdit: () => void;
  onFinishEdit: () => void;
}

function LlmRuleRowView({
  rule,
  stats,
  readOnly = false,
  onStartEdit,
  onRemove,
}: {
  rule: LlmRule;
  stats?: RuleOutcomeCounts;
  readOnly?: boolean;
  onStartEdit: () => void;
  onRemove: () => void;
}) {
  const enabled = rule.enabled !== false;
  const label = rule.label?.trim() || "Untitled rule";
  const severity = ruleSeverity(rule);

  return (
    <div
      className={clsx(
        "group flex items-center gap-2 rounded-lg border border-white/5 bg-slate-800/40 px-3 py-2.5 transition-colors",
        !enabled && "opacity-50"
      )}
      data-testid={`llm-rule-${rule.id}`}
    >
      <span
        className={clsx(
          "h-2 w-2 shrink-0 rounded-full",
          severity === "amber" ? "bg-amber-400" : "bg-rose-400"
        )}
        title={`${severity} alert`}
        aria-hidden
      />
      <button
        type="button"
        onClick={readOnly ? undefined : onStartEdit}
        disabled={readOnly}
        className={clsx(
          "flex min-w-0 flex-1 items-center gap-2 text-left",
          readOnly && "cursor-default"
        )}
        data-testid={`llm-rule-view-${rule.id}`}
      >
        <span className="shrink-0 font-semibold text-slate-100">{label}</span>
        <span className="hidden h-3 w-px shrink-0 bg-white/10 sm:block" aria-hidden />
        <span className="min-w-0 truncate text-sm text-slate-400">{rulePreview(rule.text)}</span>
        {stats && stats.total > 0 ? (
          <span
            className="shrink-0 text-xs text-slate-500"
            data-testid={`llm-rule-stats-${rule.id}`}
          >
            {formatRuleStats(stats)}
          </span>
        ) : null}
      </button>
      <FontAwesomeIcon
        icon={faBell}
        className={clsx(
          "shrink-0 text-xs",
          rule.notify ? "text-amber-400" : "text-slate-600"
        )}
        title={rule.notify ? "Notifications enabled" : "Notifications off"}
        data-testid={`llm-rule-notify-icon-${rule.id}`}
      />
      {!readOnly ? (
        <>
          <button
            type="button"
            onClick={onStartEdit}
            aria-label="Edit rule"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 opacity-0 transition-opacity hover:bg-white/5 hover:text-slate-200 group-hover:opacity-100"
            data-testid={`llm-rule-edit-${rule.id}`}
          >
            <FontAwesomeIcon icon={faPencil} className="text-xs" />
          </button>
          <RuleRemoveButton onClick={onRemove} />
        </>
      ) : null}
    </div>
  );
}

function LlmRuleRowEdit({
  rule,
  onChange,
  onRemove,
  onFinishEdit,
}: {
  rule: LlmRule;
  onChange: (rule: LlmRule) => void;
  onRemove: () => void;
  onFinishEdit: () => void;
}) {
  return (
    <div
      className="rounded-lg border border-indigo-500/20 bg-slate-800/50 p-3 ring-1 ring-indigo-500/10"
      data-testid={`llm-rule-${rule.id}`}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_auto_auto_minmax(0,1fr)_auto] sm:items-center">
        <RuleToggle
          label="Enabled"
          checked={rule.enabled !== false}
          onChange={(checked) => onChange({ ...rule, enabled: checked })}
        />
        <SeverityToggle
          value={ruleSeverity(rule)}
          onChange={(severity) => onChange({ ...rule, severity })}
        />
        <RuleToggle
          label="Notify"
          checked={rule.notify === true}
          onChange={(checked) => onChange({ ...rule, notify: checked })}
          title="Notify ops when this check fails"
          testId={`llm-rule-notify-${rule.id}`}
        />
        <FormInput
          value={rule.label ?? ""}
          onChange={(e) => onChange({ ...rule, label: e.target.value })}
          placeholder="Rule label"
          className="h-9 text-sm"
        />
        <div className="flex items-center justify-end gap-1 sm:justify-start">
          <button
            type="button"
            onClick={onFinishEdit}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:bg-white/5 hover:text-slate-100"
            data-testid={`llm-rule-done-${rule.id}`}
          >
            Done
          </button>
          <RuleRemoveButton onClick={onRemove} />
        </div>
      </div>
      <TextArea
        value={rule.text}
        onChange={(e) => onChange({ ...rule, text: e.target.value })}
        placeholder="Describe what should raise an alert when this check fails…"
        rows={3}
        className="mt-3 text-sm"
      />
    </div>
  );
}

export function LlmRuleRow({
  rule,
  editing,
  stats,
  readOnly = false,
  onChange,
  onRemove,
  onStartEdit,
  onFinishEdit,
}: LlmRuleRowProps) {
  if (editing && !readOnly) {
    return (
      <LlmRuleRowEdit
        rule={rule}
        onChange={onChange}
        onRemove={onRemove}
        onFinishEdit={onFinishEdit}
      />
    );
  }

  return (
    <LlmRuleRowView
      rule={rule}
      stats={stats}
      readOnly={readOnly}
      onStartEdit={onStartEdit}
      onRemove={onRemove}
    />
  );
}

export interface IgnoreRuleRowProps {
  rule: IgnoreRule;
  editing: boolean;
  readOnly?: boolean;
  onChange: (rule: IgnoreRule) => void;
  onRemove: () => void;
  onStartEdit: () => void;
  onFinishEdit: () => void;
}

function IgnoreRuleRowView({
  rule,
  readOnly = false,
  onStartEdit,
  onRemove,
}: {
  rule: IgnoreRule;
  readOnly?: boolean;
  onStartEdit: () => void;
  onRemove: () => void;
}) {
  const enabled = rule.enabled !== false;
  const label = rule.label?.trim() || "Untitled ignore rule";

  return (
    <div
      className={clsx(
        "group flex items-center gap-2 rounded-lg border border-slate-600/40 bg-slate-800/30 px-3 py-2.5",
        !enabled && "opacity-50"
      )}
      data-testid={`ignore-rule-${rule.id}`}
    >
      <button
        type="button"
        onClick={readOnly ? undefined : onStartEdit}
        disabled={readOnly}
        className={clsx(
          "flex min-w-0 flex-1 items-center gap-2 text-left",
          readOnly && "cursor-default"
        )}
        data-testid={`ignore-rule-view-${rule.id}`}
      >
        <span className="shrink-0 font-semibold text-slate-100">{label}</span>
        <span className="hidden h-3 w-px shrink-0 bg-white/10 sm:block" aria-hidden />
        <span className="min-w-0 truncate text-sm text-slate-400">{rulePreview(rule.text)}</span>
      </button>
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-slate-500">
        {rule.signal_type}
      </span>
      {!readOnly ? (
        <>
          <button
            type="button"
            onClick={onStartEdit}
            aria-label="Edit rule"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 opacity-0 transition-opacity hover:bg-white/5 hover:text-slate-200 group-hover:opacity-100"
          >
            <FontAwesomeIcon icon={faPencil} className="text-xs" />
          </button>
          <RuleRemoveButton onClick={onRemove} />
        </>
      ) : null}
    </div>
  );
}

function IgnoreRuleRowEdit({
  rule,
  onChange,
  onRemove,
  onFinishEdit,
}: {
  rule: IgnoreRule;
  onChange: (rule: IgnoreRule) => void;
  onRemove: () => void;
  onFinishEdit: () => void;
}) {
  return (
    <div
      className="rounded-lg border border-indigo-500/20 bg-slate-800/40 p-3 ring-1 ring-indigo-500/10"
      data-testid={`ignore-rule-${rule.id}`}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
        <RuleToggle
          label="Enabled"
          checked={rule.enabled !== false}
          onChange={(checked) => onChange({ ...rule, enabled: checked })}
        />
        <FormInput
          value={rule.label ?? ""}
          onChange={(e) => onChange({ ...rule, label: e.target.value })}
          placeholder="Ignore rule label"
          className="h-9 text-sm"
        />
        <div className="flex items-center justify-end gap-1 sm:justify-start">
          <button
            type="button"
            onClick={onFinishEdit}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:bg-white/5 hover:text-slate-100"
          >
            Done
          </button>
          <RuleRemoveButton onClick={onRemove} />
        </div>
      </div>
      <p className="mt-3 font-mono text-xs text-slate-500">signal_type: {rule.signal_type}</p>
      <TextArea
        value={rule.text}
        onChange={(e) => onChange({ ...rule, text: e.target.value })}
        placeholder="When this ignore rule applies…"
        rows={2}
        className="mt-2 text-sm"
      />
    </div>
  );
}

export function IgnoreRuleRow({
  rule,
  editing,
  readOnly = false,
  onChange,
  onRemove,
  onStartEdit,
  onFinishEdit,
}: IgnoreRuleRowProps) {
  if (editing && !readOnly) {
    return (
      <IgnoreRuleRowEdit
        rule={rule}
        onChange={onChange}
        onRemove={onRemove}
        onFinishEdit={onFinishEdit}
      />
    );
  }

  return <IgnoreRuleRowView rule={rule} readOnly={readOnly} onStartEdit={onStartEdit} onRemove={onRemove} />;
}

export function isLlmRule(rule: GuardianRule): rule is LlmRule {
  return rule.kind === "llm" || !("signal_type" in rule);
}

export function isIgnoreRule(rule: GuardianRule): rule is IgnoreRule {
  return rule.kind === "ignore";
}
